import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Folder, Tag } from 'lucide-react';

import useCategory from '../hooks/useCategory';
import useCategoryChildren from '../hooks/useCategoryChildren';
import useProducts from '../hooks/useProducts';
import Breadcrumbs from '../components/category/Breadcrumbs';
import ProductCard from '../components/product/ProductCard';
import Spinner from '../components/ui/Spinner';

// Splat route: /category/vegetables/root-vegetables/carrots
// -> useParams()['*'] === 'vegetables/root-vegetables/carrots'.
// `slug` is globally unique server-side, so only the last segment is needed
// to resolve the category; the leading segments are for URL display only.
export default function CategoryPage() {
  const { '*': splat } = useParams();
  const navigate = useNavigate();
  const segments = splat ? splat.split('/').filter(Boolean) : [];
  const lastSlug = segments[segments.length - 1] || null;
  const isRoot = !lastSlug;

  const { category, loading, error } = useCategory(lastSlug);

  // Canonical URL enforcement: once resolved, the ancestors + own slug are
  // the source of truth for the path. A stale bookmark or a category moved
  // to a different parent should redirect rather than silently render.
  useEffect(() => {
    if (!category) return;
    const canonicalPath = `/category/${[...(category.ancestors || []).map((a) => a.slug), category.slug].join('/')}`;
    if (canonicalPath !== `/category/${segments.join('/')}`) {
      navigate(canonicalPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Root index and any non-leaf category show a subcategory chip row for
  // refinement. Any resolved category (leaf or not) also shows a product
  // grid — the server expands a non-leaf category filter to every product
  // filed under its descendants (GET /product?category=), so "Test" surfaces
  // products from "Test 2" and "Test 3" the same way "Test 2" alone would.
  const showChildren = isRoot || (!!category && !category.isLeaf);
  const showProducts = !!category;

  const { categories: children, loading: childrenLoading } = useCategoryChildren(
    category ? category._id : null,
    showChildren,
  );
  const { items: products, loading: productsLoading, error: productsError } = useProducts({
    category: category ? category.slug : undefined,
    enabled: showProducts,
  });

  if (lastSlug && loading) {
    return (
      <div className="ss-container ss-state">
        <Spinner label="Loading category" size={32} />
      </div>
    );
  }

  if (lastSlug && (error || !category)) {
    return (
      <section className="ss-container ss-state">
        <h2>Category not found</h2>
        <p>This category may have been removed or renamed.</p>
      </section>
    );
  }

  return (
    <section className="ss-container ss-section">
      <Breadcrumbs ancestors={category ? category.ancestors : []} current={category} />
      <h1>{category ? category.name : 'Categories'}</h1>

      {showChildren && (
        childrenLoading ? (
          <div className="ss-state"><Spinner label="Loading categories" size={32} /></div>
        ) : children.length === 0 ? (
          <div className="ss-state">
            <h2>Coming soon</h2>
            <p>We’re still growing this category. Check back soon!</p>
          </div>
        ) : (
          <div className="ss-chips" role="group" aria-label="Subcategories">
            {children.map((c) => (
              <Link
                key={c._id}
                to={`/category/${[...segments, c.slug].join('/')}`}
                className={`ss-cat-chip ${c.isLeaf ? '' : 'ss-cat-chip--folder'}`.trim()}
                title={c.isLeaf ? 'View products' : 'Browse subcategories'}
              >
                {c.isLeaf ? <Tag size={14} aria-hidden="true" /> : <Folder size={14} aria-hidden="true" />}
                {c.name}
              </Link>
            ))}
          </div>
        )
      )}

      {showProducts && (
        <>
          {showChildren && <h2 style={{ marginTop: 'var(--ss-space-6)' }}>Products</h2>}
          {productsLoading ? (
            <div className="ss-state"><Spinner label="Loading products" size={32} /></div>
          ) : productsError ? (
            <div className="ss-state">
              <h2>Couldn’t load products</h2>
              <p>Please try again in a moment.</p>
            </div>
          ) : products.length === 0 ? (
            <div className="ss-state">
              <h2>Coming soon</h2>
              <p>We’re still growing this category. Check back soon!</p>
            </div>
          ) : (
            <div className="ss-product-grid">
              {products.map((p) => (
                <ProductCard key={p._id || p.id} product={p} />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
